import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { useContext } from 'react'
import ReturnsContext from '../../../context'
import { getProductCategory } from '@helpers/products'
import { RETURN_REASON_CODES } from '@components/returns/consts'
import { Fit } from 'types/fit'
import { objectKeys } from '@helpers/utils'
import { Values } from '@components/returns/types'
import styles from './styles.module.scss'
import { ProductSizes } from '@api/returns/types'

const ALLOWED_PRODUCT_CATEGORIES = ['Trousers', 'Denim', 'Tops']

export function productSizesMatchCustomerFit({
  sizes,
  customerFit
}: {
  sizes: Partial<ProductSizes>
  customerFit?: Fit
}) {
  if (!customerFit || !sizes) {
    return false
  }

  const sizesKeys = objectKeys(sizes).filter(key => key !== 'taper')

  return sizesKeys.every(size => {
    const transformSize = size.replace('shortsleg', 'leg') as keyof Fit
    if (
      sizes[size] === customerFit[transformSize] &&
      objectKeys(customerFit).find(value => value === transformSize)
    ) {
      return true
    }
    return false
  })
}

function getMatchingCustomerFitSizes(
  customerFit: Fit | undefined,
  productSizes: Values['sizes']
) {
  const sizes: Values['sizes'] = {}
  objectKeys(productSizes)
    .filter(productSize =>
      customerFit
        ? !!customerFit[productSize as keyof typeof customerFit]
        : false
    )
    .forEach(productSize => {
      if (customerFit) {
        sizes[productSize] =
          customerFit[productSize as keyof typeof customerFit]
      }
    })
  return sizes
}

function SavedFitOptions({ sizes }: { sizes: Values['sizes'] }) {
  return (
    <div className={styles.ReturnsSavedFitBox}>
      <h4 className={styles.ReturnsSavedFitBox__title}>Your Fit</h4>
      <div className={styles.ReturnsSavedFitBox__options}>
        {objectKeys(sizes).map(type => (
          <div className={styles.ReturnsSavedFitBox__option} key={type}>
            <span className={styles.ReturnsSavedFitBox__optionType}>
              {type}
            </span>
            <span className={styles.ReturnsSavedFitBox__optionValue}>
              {sizes[type]}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const copy = {
  default: {
    title: 'Thought about exchanging?',
    description: '80% of people who do, keep their exchanged item.',
    buttonText: "OK, I'll exchange"
  },
  withSavedFit: {
    title: 'Exchange for your saved fit',
    description: 'You bought in a size which is not your saved fit',
    buttonText: 'Exchange for this size'
  }
}

const checkIsAllowedProductCategory = (
  sizes: Partial<Values['sizes']>,
  productType: string
) => {
  const updatedSizes = objectKeys(sizes).map(size => {
    return {
      type: size
    }
  })
  const category = getProductCategory(updatedSizes, productType)
  return category && ALLOWED_PRODUCT_CATEGORIES.includes(category)
}

type ExchangeNotificationProps = {
  type: 'exchange' | 'refund'
  productSizes: Partial<ProductSizes>
  productType: string
  reason: number | undefined
  onExchange: (args: {
    sizes?: Values['sizes'] & { available?: boolean }
    step: number
  }) => void
}

function ExchangeNotification({
  type,
  productSizes,
  productType,
  reason,
  onExchange
}: ExchangeNotificationProps) {
  const { customerFit } = useContext(ReturnsContext)

  const sizes = getMatchingCustomerFitSizes(customerFit, productSizes)

  const isAllowedProductCategory = checkIsAllowedProductCategory(
    productSizes,
    productType
  )

  const matchesCustomerFit = productSizesMatchCustomerFit({
    sizes: productSizes,
    customerFit: customerFit
  })

  const isFitReason =
    reason === undefined ||
    reason === RETURN_REASON_CODES.FIT_NOT_RIGHT ||
    reason === RETURN_REASON_CODES.LENGTH_NOT_RIGHT

  const shouldShowSavedFitOptions =
    !matchesCustomerFit && isFitReason && isAllowedProductCategory

  const handleButtonClick = () => {
    if (shouldShowSavedFitOptions) {
      // Since SavedFit does not contain "taper" as a fit size,
      // we need to set the intial sizes and then update with the new size values
      const sizesToExchange = {
        ...productSizes,
        ...sizes,
        available: true
      }
      onExchange({ sizes: sizesToExchange, step: 1 })
    } else {
      onExchange({ step: 0 })
    }
  }

  if (!shouldShowSavedFitOptions && type === 'exchange') return null

  const bodyCopy = copy[shouldShowSavedFitOptions ? 'withSavedFit' : 'default']

  return (
    <div className={styles.ReturnExchangeNotification}>
      <h3>{bodyCopy.title}</h3>
      <p>{bodyCopy.description}</p>

      {shouldShowSavedFitOptions && <SavedFitOptions sizes={sizes} />}

      <ButtonMinimal onClick={handleButtonClick} underlined>
        {bodyCopy.buttonText}
      </ButtonMinimal>
    </div>
  )
}

export default ExchangeNotification
